import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    Typography,
    TextField,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { kioskModel } from '../Axios/AxiosConfig';
import { toast } from 'react-toastify';
import Cookies from 'js-cookie';

const Settings = () => {
    const getKioskDetails = () => {
        const kioskDetailsCookie = Cookies.get('kiosk_details');
        if (kioskDetailsCookie) {
            try {
                return JSON.parse(kioskDetailsCookie);
            } catch (error) {
                console.error('Error parsing kiosk_details cookie:', error);
            }
        }
        return null;
    };

    const getKioskUID = () => {
        const details = getKioskDetails();
        return details ? (details.UID || details.id) : '';
    };

    const [isEditing, setIsEditing] = useState(false);

    const parseColorToHtml = (colorStr) => {
        if (colorStr && colorStr.startsWith('0xFF')) {
            return '#' + colorStr.substring(4);
        }
        return colorStr;
    };

    const parseColorToFlutter = (colorStr) => {
        if (colorStr && colorStr.startsWith('#')) {
            return '0xFF' + colorStr.substring(1).toUpperCase();
        }
        return colorStr;
    };

    const [settings, setSettings] = useState({
        ThemeColors: [
            { key: 'themePrimaryColor', val: '0xFFDA6E0B' }, 
            { key: 'themeSecondaryColor', val: '0xFF75623F' }
        ],
        SocialMediaLinks: [],
        company_url: '',
        company: '',
        image: '',
        image_base64: null
    });
    const [loading, setLoading] = useState(true);
    const [savingSettings, setSavingSettings] = useState(false);
    const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);

    useEffect(() => {
        if (getKioskUID()) {
            fetchSettings();
        } else {
            setLoading(false);
        }
    }, []);

    const fetchSettings = async () => {
        setLoading(true);
        try {
            const response = await kioskModel.get(`/api/v1/getKioskSettings/${getKioskUID()}`);
            if (response.status === 200 && response.data) {
                const defaultColors = [
                    { key: 'themePrimaryColor', val: '0xFFDA6E0B' }, 
                    { key: 'themeSecondaryColor', val: '0xFF75623F' }
                ];
                setSettings({
                    ThemeColors: response.data.ThemeColors && response.data.ThemeColors.length > 0 ? response.data.ThemeColors : defaultColors,
                    SocialMediaLinks: response.data.SocialMediaLinks && response.data.SocialMediaLinks.length > 0 ? response.data.SocialMediaLinks : [],
                    company_url: response.data.company_url || '',
                    company: response.data.company || '',
                    image: response.data.image || ''
                });
            }
        } catch (error) {
            console.error('Error fetching settings:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSaveSettings = async () => {
        setSavingSettings(true);
        try {
            const payload = { ...settings, kiosk_id: getKioskUID() };
            const response = await kioskModel.put('/api/v1/updateKioskSettings', payload);
            if (response.status === 200) {
                toast.success('Settings updated successfully');
                setIsEditing(false);
                
                // Update cookie
                let currentDetails = getKioskDetails();
                if (currentDetails) {
                    currentDetails.company = settings.company;
                    currentDetails.ThemeColors = settings.ThemeColors;
                    currentDetails.SocialMediaLinks = settings.SocialMediaLinks;
                    currentDetails.company_url = settings.company_url;
                    if(response.data.data && response.data.data.image) {
                        currentDetails.image = response.data.data.image;
                    } else if (settings.image && !settings.image.startsWith('data:')) {
                        currentDetails.image = settings.image;
                    }
                    Cookies.set('kiosk_details', JSON.stringify(currentDetails), { path: '/', expires: 30 });
                    
                    // Immediately apply globally to the dashboard
                    if (settings.ThemeColors && Array.isArray(settings.ThemeColors)) {
                        settings.ThemeColors.forEach(color => {
                            const htmlColor = color.val && color.val.startsWith('0xFF') 
                                ? '#' + color.val.substring(4) 
                                : color.val;
                            if (htmlColor) {
                                document.documentElement.style.setProperty(`--${color.key}`, htmlColor);
                            }
                        });
                        window.dispatchEvent(new Event('themeChanged'));
                    }
                }
            }
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to save settings');
        } finally {
            setSavingSettings(false);
        }
    };

    const handleGeneratePreview = async () => {
        setPreviewLoading(true);
        try {
            const payload = {
                themeColors: settings.ThemeColors,
                company: settings.company,
                company_url: settings.company_url,
                socialMediaLinks: settings.SocialMediaLinks,
                kiosk_id: getKioskUID()
            };
            const response = await kioskModel.post('/api/v1/previewKioskReport', payload, {
                responseType: 'blob'
            });
            const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
            setPreviewPdfUrl(url);
        } catch (error) {
            console.error('Error generating preview:', error);
            toast.error('Failed to generate preview PDF');
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleAddColor = () => {
        setSettings(prev => ({
            ...prev,
            ThemeColors: [...prev.ThemeColors, { key: 'newThemeColor', val: '0xFF000000' }]
        }));
    };

    const handleRemoveColor = (index) => {
        setSettings(prev => {
            const newColors = [...prev.ThemeColors];
            newColors.splice(index, 1);
            return { ...prev, ThemeColors: newColors };
        });
    };

    const handleAddSocialLink = () => {
        setSettings(prev => ({
            ...prev,
            SocialMediaLinks: [...prev.SocialMediaLinks, { type: '', link: '' }]
        }));
    };

    const handleRemoveSocialLink = (index) => {
        setSettings(prev => {
            const newLinks = [...prev.SocialMediaLinks];
            newLinks.splice(index, 1);
            return { ...prev, SocialMediaLinks: newLinks };
        });
    };

    const handleImageUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setSettings({ ...settings, image_base64: reader.result, image: reader.result });
            };
            reader.readAsDataURL(file);
        }
    };

    const primaryColorVal = settings.ThemeColors.find(c => c.key === 'themePrimaryColor')?.val || '0xFFDA6E0B';
    const secondaryColorVal = settings.ThemeColors.find(c => c.key === 'themeSecondaryColor')?.val || '0xFF75623F';
    const tertiaryColorVal = settings.ThemeColors.find(c => c.key === 'themeTertiaryColor')?.val || '0xFFDA6E0B';
    const backgroundColorVal = settings.ThemeColors.find(c => c.key === 'themeBackgroundColor')?.val || '0xFFF5F5F5';
    const surfaceColorVal = settings.ThemeColors.find(c => c.key === 'themeSurfaceColor')?.val || '0xFFFFFFFF';
    
    const primaryColor = parseColorToHtml(primaryColorVal);
    const secondaryColor = parseColorToHtml(secondaryColorVal);
    const tertiaryColor = parseColorToHtml(tertiaryColorVal);
    const backgroundColor = parseColorToHtml(backgroundColorVal);
    const surfaceColor = parseColorToHtml(surfaceColorVal);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                <CircularProgress sx={{ color: 'var(--themePrimaryColor, #DA6E0B)' }} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, mb: 8 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Typography
                    sx={{
                        fontFamily: 'Libre Baskerville, serif',
                        fontWeight: 700,
                        fontSize: '24px',
                        color: 'var(--themePrimaryColor, #312110)'
                    }}
                >
                    App Configuration
                </Typography>
                {!isEditing ? (
                    <Button variant="outlined" onClick={() => setIsEditing(true)} sx={{ color: 'var(--themePrimaryColor, #312110)', bordercolor: 'var(--themePrimaryColor, #312110)', fontFamily: 'Poppins' }}>
                        Edit Settings
                    </Button>
                ) : (
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Button variant="text" onClick={() => { setIsEditing(false); fetchSettings(); }} disabled={savingSettings} sx={{ color: '#666', fontFamily: 'Poppins' }}>
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={handleSaveSettings} disabled={savingSettings} sx={{ bgcolor: primaryColor, '&:hover': { opacity: 0.9 }, fontFamily: 'Poppins' }}>
                            {savingSettings ? <CircularProgress size={24} color="inherit" /> : 'Save Settings'}
                        </Button>
                    </Box>
                )}
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} lg={6}>
                    <Card sx={{ borderRadius: '8px', backgroundColor: 'var(--themeSecondaryColor, #efefe7)', boxShadow: 'none', height: '100%' }}>
                        <CardContent sx={{ p: 3 }}>
                            <Typography sx={{ fontFamily: 'Poppins', fontWeight: 500, fontSize: '18px', mb: 3 }}>General Details</Typography>
                            
                            {!isEditing ? (
                                <Box sx={{ mb: 3 }}>
                                    <Typography sx={{ color: 'var(--themePrimaryColor, #312110)', fontFamily: 'Poppins', fontSize: '14px', mb: 0.5 }}>Company Name</Typography>
                                    <Typography sx={{ fontFamily: 'Poppins', mb: 2, fontWeight: 500 }}>{settings.company || 'Not set'}</Typography>
                                    
                                    <Typography sx={{ color: 'var(--themePrimaryColor, #312110)', fontFamily: 'Poppins', fontSize: '14px', mb: 0.5 }}>Company URL</Typography>
                                    <Typography sx={{ fontFamily: 'Poppins', mb: 2, fontWeight: 500 }}>{settings.company_url || 'Not set'}</Typography>

                                    <Typography sx={{ color: 'var(--themePrimaryColor, #312110)', fontFamily: 'Poppins', fontSize: '14px', mb: 0.5 }}>Company Logo</Typography>
                                    {settings.image ? <img src={settings.image} alt="Logo" style={{ height: '60px', marginBottom: '16px' }} /> : <Typography sx={{ fontFamily: 'Poppins', mb: 2, fontWeight: 500 }}>Not set</Typography>}

                                    <Typography sx={{ color: 'var(--themePrimaryColor, #312110)', fontFamily: 'Poppins', fontSize: '14px', mb: 0.5, mt: 3 }}>Theme Colors</Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4, mb: 2 }}>
                                        {settings.ThemeColors.map((colorObj, idx) => {
                                            const htmlColor = parseColorToHtml(colorObj.val);
                                            return (
                                                <Box key={idx}>
                                                    <Typography sx={{ fontSize: '12px', mb: 1 }}>{colorObj.key}</Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: htmlColor, border: '1px solid #ccc' }} />
                                                        <Typography sx={{ fontFamily: 'Poppins', fontWeight: 500 }}>{colorObj.val}</Typography>
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Box>

                                    <Typography sx={{ color: 'var(--themePrimaryColor, #312110)', fontFamily: 'Poppins', fontSize: '14px', mb: 1, mt: 3 }}>Social Media Links</Typography>
                                    {settings.SocialMediaLinks.map((item, idx) => (
                                        item.link ? (
                                            <Typography key={idx} sx={{ fontFamily: 'Poppins', mb: 1, fontWeight: 500, wordBreak: 'break-all' }}>
                                                {item.type ? `${item.type}: ` : ''}{item.link}
                                            </Typography>
                                        ) : null
                                    ))}
                                    {settings.SocialMediaLinks.length === 0 && <Typography sx={{ fontFamily: 'Poppins' }}>Not set</Typography>}
                                </Box>
                            ) : (
                                <Box>
                                    <TextField fullWidth label="Company Name" value={settings.company} onChange={e => setSettings({...settings, company: e.target.value})} sx={{ mb: 2 }} />
                                    <TextField fullWidth label="Company URL" value={settings.company_url} onChange={e => setSettings({...settings, company_url: e.target.value})} sx={{ mb: 2 }} />
                                    
                                    <Box sx={{ mb: 2 }}>
                                        <Typography sx={{ fontFamily: 'Poppins', mb: 1, color: 'text.secondary', fontSize: '14px' }}>Company Logo</Typography>
                                        {settings.image && <img src={settings.image} alt="Logo" style={{ height: '60px', marginBottom: '8px', display: 'block' }} />}
                                        <Button variant="outlined" component="label" sx={{ textTransform: 'none', fontFamily: 'Poppins', color: primaryColor, borderColor: primaryColor }}>
                                            Upload Logo
                                            <input type="file" hidden accept="image/*" onChange={handleImageUpload} />
                                        </Button>
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 1 }}>
                                        <Typography sx={{ fontFamily: 'Poppins' }}>Theme Colors</Typography>
                                        <Button startIcon={<Add />} onClick={handleAddColor} sx={{ textTransform: 'none', color: primaryColor, fontFamily: 'Poppins' }}>Add Color</Button>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                                        {settings.ThemeColors.map((colorObj, idx) => (
                                            <Box key={idx} sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                                <FormControl sx={{ flex: 1 }}>
                                                    <InputLabel>Key</InputLabel>
                                                    <Select
                                                        value={colorObj.key}
                                                        label="Key"
                                                        onChange={e => {
                                                            const newColors = [...settings.ThemeColors];
                                                            newColors[idx].key = e.target.value;
                                                            setSettings({...settings, ThemeColors: newColors});
                                                        }}
                                                    >
                                                        <MenuItem value="themePrimaryColor">Primary</MenuItem>
                                                        <MenuItem value="themeSecondaryColor">Secondary</MenuItem>
                                                        <MenuItem value="themeTertiaryColor">Tertiary</MenuItem>
                                                        <MenuItem value="themeBackgroundColor">Background</MenuItem>
                                                        <MenuItem value="themeSurfaceColor">Surface</MenuItem>
                                                        <MenuItem value="newThemeColor">Custom</MenuItem>
                                                    </Select>
                                                </FormControl>
                                                <TextField 
                                                    type="color" 
                                                    label="Color" 
                                                    value={parseColorToHtml(colorObj.val)} 
                                                    onChange={e => {
                                                        const newColors = [...settings.ThemeColors];
                                                        newColors[idx].val = parseColorToFlutter(e.target.value);
                                                        setSettings({...settings, ThemeColors: newColors});
                                                    }} 
                                                    sx={{ flex: 1 }} 
                                                />
                                                <IconButton onClick={() => handleRemoveColor(idx)} color="error">
                                                    <Delete />
                                                </IconButton>
                                            </Box>
                                        ))}
                                    </Box>

                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 1 }}>
                                        <Typography sx={{ fontFamily: 'Poppins' }}>Social Media Links</Typography>
                                        <Button startIcon={<Add />} onClick={handleAddSocialLink} sx={{ textTransform: 'none', color: primaryColor, fontFamily: 'Poppins' }}>Add Link</Button>
                                    </Box>
                                    {settings.SocialMediaLinks.map((linkObj, idx) => (
                                        <Box key={idx} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                                            <FormControl sx={{ width: '150px' }}>
                                                <InputLabel>Type</InputLabel>
                                                <Select
                                                    value={linkObj.type || ''}
                                                    label="Type"
                                                    onChange={e => {
                                                        const newLinks = [...settings.SocialMediaLinks];
                                                        newLinks[idx].type = e.target.value;
                                                        setSettings({...settings, SocialMediaLinks: newLinks});
                                                    }}
                                                >
                                                    <MenuItem value="Facebook">Facebook</MenuItem>
                                                    <MenuItem value="Instagram">Instagram</MenuItem>
                                                    <MenuItem value="Twitter">Twitter</MenuItem>
                                                    <MenuItem value="LinkedIn">LinkedIn</MenuItem>
                                                    <MenuItem value="Website">Website</MenuItem>
                                                    <MenuItem value="Other">Other</MenuItem>
                                                </Select>
                                            </FormControl>
                                            <TextField fullWidth label="Link" value={linkObj.link || ''} onChange={e => {
                                                const newLinks = [...settings.SocialMediaLinks];
                                                newLinks[idx] = { ...newLinks[idx], link: e.target.value };
                                                setSettings({...settings, SocialMediaLinks: newLinks});
                                            }} />
                                            <IconButton onClick={() => handleRemoveSocialLink(idx)} color="error">
                                                <Delete />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
                
                <Grid item xs={12} lg={6}>
                    <Card sx={{ borderRadius: '8px', boxShadow: 'none', border: `2px solid ${primaryColor}`, height: '100%' }}>
                        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography sx={{ color: secondaryColor, fontFamily: 'Poppins', fontWeight: 600 }}>App Theme Preview (PDF)</Typography>
                                <Button 
                                    variant="outlined" 
                                    onClick={handleGeneratePreview} 
                                    disabled={previewLoading}
                                    sx={{ textTransform: 'none', fontFamily: 'Poppins', borderColor: primaryColor, color: primaryColor }}
                                >
                                    {previewLoading ? <CircularProgress size={20} color="inherit" /> : 'Generate Preview'}
                                </Button>
                            </Box>
                            
                            <Box sx={{ flex: 1, border: `1px solid #e0e0e0`, borderRadius: '8px', overflow: 'hidden', minHeight: '500px', display: 'flex', justifyContent: 'center', alignItems: 'center', bgcolor: '#f5f5f5' }}>
                                {previewPdfUrl ? (
                                    <iframe 
                                        src={previewPdfUrl} 
                                        width="100%" 
                                        height="100%" 
                                        style={{ border: 'none', minHeight: '500px' }} 
                                        title="PDF Preview"
                                    />
                                ) : (
                                    <Typography sx={{ fontFamily: 'Poppins', color: '#888' }}>Click "Generate Preview" to see the full PDF with current settings</Typography>
                                )}
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Settings;
